import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router';
import { ErrorCode } from '@brimo/shared';
import { Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { isPortalUnauthenticated, usePortalMe } from '../api';
import type { PortalMe } from '../types';
import { PortalFrame } from './PortalFrame';
import styles from './Portal.module.css';

export interface PortalContextValue {
  me: PortalMe;
  slug: string;
  /** Portal path of a section: `path('documents')` → `/portal/<slug>/documents`. */
  path: (section?: string) => string;
  /** Opens the chat (the docked panel on /questions, the full-screen sheet below 768 px). */
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

/** The portal context, or null outside the signed-in portal (the staff preview „Portalansicht öffnen"). */
export function usePortalOptional(): PortalContextValue | null {
  return useContext(PortalContext);
}

export function usePortal(): PortalContextValue {
  const value = useContext(PortalContext);
  if (!value) throw new Error('usePortal outside the portal layout');
  return value;
}

/**
 * `/portal/:orgSlug/*` — the signed-in portal (Phase B). Loads `GET /api/portal/me` for the frame; a 401
 * `PORTAL_UNAUTHENTICATED` of any portal query sends the customer to the organization's login, a 403
 * `MODULE_DISABLED` shows the notice page (own), a session of another organization is redirected to its slug.
 */
export function PortalLayout() {
  const { orgSlug = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = usePortalMe();
  const [chatOpen, setChatOpen] = useState(false);
  const loginPath = `/portal/${orgSlug}/login`;

  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const query = event.query as { queryKey: readonly unknown[]; state: { error: unknown } };
      if (query.queryKey[0] === 'portal' && isPortalUnauthenticated(query.state.error)) {
        queryClient.removeQueries({ queryKey: ['portal'] });
        void navigate(loginPath, { replace: true });
      }
    });
  }, [queryClient, navigate, loginPath]);

  const path = useCallback(
    (section?: string) => (section ? `/portal/${orgSlug}/${section}` : `/portal/${orgSlug}`),
    [orgSlug],
  );

  const value = useMemo<PortalContextValue | null>(
    () => (me.data ? { me: me.data, slug: orgSlug, path, chatOpen, setChatOpen } : null),
    [me.data, orgSlug, path, chatOpen],
  );

  if (me.isError) {
    if (isPortalUnauthenticated(me.error)) return <Navigate to={loginPath} replace />;
    const disabled = me.error instanceof ApiError && me.error.code === ErrorCode.MODULE_DISABLED;
    return (
      <div className={styles.noticePage}>
        <div className={styles.noticeCard} role="alert">
          <div className={styles.noticeBrand}>DAMOKLES</div>
          <h1 className={styles.noticeTitle}>
            {disabled ? 'Das Mandantenportal ist derzeit nicht verfügbar.' : errorMessage(me.error)}
          </h1>
          <p className={styles.noticeText}>
            {disabled
              ? 'Bitte wenden Sie sich direkt an Ihren Berater.'
              : 'Bitte laden Sie die Seite in einem Moment erneut.'}
          </p>
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <div className={styles.loadingPage} aria-busy="true">
        <div className={styles.loadingBar} />
        <div className={styles.loadingBody}>
          <Skeleton width="30%" height={28} />
          <Skeleton height={220} />
          <Skeleton height={160} />
        </div>
      </div>
    );
  }

  if (value.me.organization.slug !== orgSlug) {
    return <Navigate to={`/portal/${value.me.organization.slug}`} replace />;
  }

  return (
    <PortalContext.Provider value={value}>
      <PortalFrame>
        <Outlet />
      </PortalFrame>
    </PortalContext.Provider>
  );
}
